import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Resume List page
 * Provides guided help for common resume management actions
 */
export const useResumeListQuickHelp = () => {
    const options: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'resume-create',
                label: 'Thêm hồ sơ ứng viên',
                description: 'Hướng dẫn thêm hồ sơ mới',
                selector: '[data-tour="create-resume-btn"]',
                tourTitle: 'Thêm hồ sơ ứng viên',
                tourDescription:
                    'Nhấn nút "Tạo mới" để thêm hồ sơ ứng viên:\n\n• Nhập thông tin cá nhân (tên, email, SĐT)\n• Chọn vị trí ứng tuyển\n• Chọn phòng ban\n• Upload file CV (PDF/Word)\n• Chọn nguồn CV (LinkedIn, Website, ...)\n• Ghi chú đánh giá ban đầu\n\nHồ sơ sẽ được lưu và sẵn sàng cho bước sàng lọc.',
            },
            {
                id: 'resume-screen',
                label: 'Sàng lọc hồ sơ',
                description: 'Đánh giá và sàng lọc CV',
                selector: '[data-tour="resume-data-table"]',
                tourTitle: 'Sàng lọc hồ sơ',
                tourDescription:
                    'Quy trình sàng lọc hồ sơ:\n\n1. Click vào ID/Tên để xem chi tiết hồ sơ\n2. Xem thông tin và CV của ứng viên\n3. Chọn kết quả sàng lọc:\n   • PASS - Hồ sơ đạt yêu cầu\n   • FAIL - Hồ sơ không phù hợp\n   • CLOSE JOB - Đóng vị trí tuyển dụng\n4. Cập nhật trạng thái cuối:\n   • Interview - Mời phỏng vấn\n   • Pass/Fail Interview\n   • Tuyển dụng\n\nHệ thống sẽ tự động gửi email thông báo cho ứng viên.',
            },
            {
                id: 'resume-interview',
                label: 'Lên lịch phỏng vấn',
                description: 'Đặt lịch và theo dõi phỏng vấn',
                tourTitle: 'Quản lý phỏng vấn',
                tourDescription:
                    'Để lên lịch phỏng vấn:\n\n1. Chọn hồ sơ có kết quả sàng lọc "PASS"\n2. Chọn trạng thái cuối "Interview"\n3. Chọn thời gian phỏng vấn\n4. Nhập địa điểm/link phỏng vấn\n5. Gửi email mời phỏng vấn\n\n**Theo dõi:**\n• Xem danh sách phỏng vấn theo ngày\n• Cập nhật kết quả sau phỏng vấn\n• Ghi chú đánh giá của interviewer',
            },
            {
                id: 'resume-filter',
                label: 'Lọc hồ sơ',
                description: 'Sử dụng bộ lọc nâng cao',
                selector: '[data-tour="resume-filter-bar"]',
                tourTitle: 'Bộ lọc hồ sơ',
                tourDescription:
                    'Lọc hồ sơ theo nhiều tiêu chí:\n\n**Vị trí ứng tuyển:**\nChọn vị trí cần xem (Backend, Frontend, ...)\n\n**Nguồn CV:**\nLọc theo nguồn (LinkedIn, Website, Referral, ...)\n\n**Phòng ban:**\nLọc theo phòng ban (TECH, DEV, HR, ...)\n\n**Ngày ứng tuyển:**\nChọn khoảng thời gian nộp hồ sơ\n\n**Tips:** Kết hợp nhiều bộ lọc để tìm chính xác ứng viên cần thiết.',
            },
            {
                id: 'resume-search',
                label: 'Tìm kiếm ứng viên',
                description: 'Tìm nhanh theo tên/email',
                selector: '[data-tour="resume-search"]',
                tourTitle: 'Tìm kiếm hồ sơ',
                tourDescription:
                    'Tìm kiếm nhanh hồ sơ ứng viên:\n\n• Nhập tên ứng viên\n• Nhập email\n• Nhập số điện thoại\n• Nhập vị trí ứng tuyển\n\nKết quả hiển thị ngay khi bạn gõ. Tìm kiếm kết hợp với bộ lọc để thu hẹp kết quả.',
            },
            {
                id: 'resume-export',
                label: 'Xuất danh sách',
                description: 'Export Excel danh sách hồ sơ',
                selector: '[data-tour="resume-settings"]',
                tourTitle: 'Export danh sách',
                tourDescription:
                    'Xuất danh sách hồ sơ ra Excel:\n\n1. Click icon Settings (⚙️)\n2. Click nút "Download" trong phần Actions\n3. File Excel sẽ được tải xuống\n\n**Nội dung file:**\n• Thông tin ứng viên đầy đủ\n• Theo bộ lọc hiện tại\n• Kết quả sàng lọc\n• Trạng thái phỏng vấn\n\nHữu ích cho báo cáo tuyển dụng định kỳ.',
            },
            {
                id: 'resume-manage-jobs',
                label: 'Quản lý tin tuyển dụng',
                description: 'Tạo và quản lý vị trí tuyển dụng',
                selector: '[data-tour="manage-jobs-btn"]',
                tourTitle: 'Quản lý tin tuyển dụng',
                tourDescription:
                    'Nhấn "Quản lý tin tuyển dụng" để:\n\n• Tạo tin tuyển dụng mới\n• Chỉnh sửa mô tả công việc\n• Đóng/mở tin tuyển dụng\n• Xem số lượng ứng viên theo từng vị trí\n• Đăng tin lên website công ty\n\nTin tuyển dụng được liên kết với danh sách hồ sơ ứng tuyển.',
            },
            {
                id: 'resume-table-settings',
                label: 'Tuỳ chỉnh bảng',
                description: 'Cài đặt hiển thị bảng',
                selector: '[data-tour="resume-settings"]',
                tourTitle: 'Cài đặt bảng',
                tourDescription:
                    'Click icon Settings (⚙️) để:\n\n• Điều chỉnh cỡ chữ (Nhỏ/Vừa/Lớn)\n• Thay đổi chiều cao hàng\n• Ẩn/hiện các cột\n• Sắp xếp thứ tự cột\n• Reset về mặc định\n\n**Lưu ý:** Cài đặt được lưu tự động vào trình duyệt và giữ nguyên khi bạn quay lại.',
            },
            {
                id: 'resume-sort',
                label: 'Sắp xếp danh sách',
                description: 'Sắp xếp theo cột',
                selector: '[data-tour="resume-data-table"]',
                tourTitle: 'Sắp xếp danh sách',
                tourDescription:
                    'Click vào tiêu đề cột để sắp xếp:\n\n**Các cột có thể sắp xếp:**\n• ID\n• Tên ứng viên\n• Email\n• Vị trí ứng tuyển\n• Phòng ban\n• Loại vị trí\n• Ngày ứng tuyển\n• Ngày cập nhật\n• Nguồn CV\n• Kết quả sàng lọc\n• Trạng thái cuối\n• Thời gian phỏng vấn\n\nClick lần 2 để đảo ngược thứ tự (tăng/giảm dần).',
            },
            {
                id: 'resume-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng quản lý hồ sơ ứng viên.',
            },
        ],
        []
    )

    useQuickHelp(options)

    return { options }
}

export default useResumeListQuickHelp
